﻿import React from 'react';

import { LicenseInfoDialog } from 'components/LicenseInfoDialog/LicenseInfoDialog';
import { Report, ReportApi, ReportComponent, ResultItem } from 'apis/ReportApi';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';
import { Col } from 'react-bootstrap';

import 'pages/reports/components/ChartActionItem/styles/chart-action-item.scss';

type ChartActionItemProps = {
    component: ReportComponent;
    index: number;
    isSubReport?: boolean;
    loading: boolean;
    onActionButtonClick?: React.Dispatch<React.SetStateAction<boolean>>;
    setCurrentActionData: React.Dispatch<React.SetStateAction<ReportComponent | undefined>>;
    setCurrentSubReport: React.Dispatch<React.SetStateAction<Report | undefined>>;
    setShowActionModal: React.Dispatch<React.SetStateAction<boolean>>;
    showActionButton?: boolean;
};

export const ChartActionItem = (props: ChartActionItemProps) => {

    const { formatMessage } = useIntl();

    const {
        component,
        index,
        isSubReport = false,
        loading,
        setCurrentSubReport,
        setShowActionModal
    } = props;

    const [state, setState] = useSuperState({
        currentDialog: null as React.ReactNode,
        embeddedURL: '',
        selectedReport: undefined as ResultItem | undefined,
        showModal: false
    });

    const { currentDialog, embeddedURL, selectedReport, showModal } = state;

    // Careful, these two functions are being used by the "Eval" function down below. DO NOT DELETE
    //-------
    const RunSubReport = (scopeObjectId: number, reportId: number, subReportComponentId: number) => {

        setCurrentSubReport(undefined);
        ReportApi.GetReportLayout(reportId, scopeObjectId, 1, true)
            .then(report => {

                setCurrentSubReport(report);

            })
            .catch(console.error);

    };

    const OpenURL = (url: string, target: string) => {

        if (target === 'modal') {

            setState({ embeddedURL: url, showModal: true });

        } else if (target === 'current') {

            window.open(url, '_self');

        } else {

            window.open(url, target);

        }

    };

    const OpenDialog = (component: string, data: any) => {

        switch (component) {

            case 'UsesRemainingDialog':
                setState({
                    currentDialog: (
                        <LicenseInfoDialog
                            transactionDetails={data.transactionId}
                        />
                    )
                });
                break;

            default:
                break;

        }

    };

    //--------

    const handleItemSelect = (item: ResultItem) => {

        setState({ selectedReport: item });
        item.Action && eval(item.Action);

    };

    const renderContent = () => (
        <div className="chart-action-item">
            <h6 className="chart-action-item__title">
                {component.Title}
            </h6>
            <div className="chart-action-item__list">
                {component.Results?.SeriesList.length === 0 ? (
                    <div className="chart-action-item__empty">
                        <i aria-hidden="true" className="fa-solid fa-box-open chart-action-item__empty-icon"></i>
                        <span className="chart-action-item__empty-title">
                            <FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" />
                        </span>
                        <span className="chart-action-item__empty-reason">
                            <FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" />
                        </span>
                    </div>
                ) : (
                    component.Results?.SeriesList.map((dataList, parentIndex) => (
                        <ul
                            aria-label={component.Title}
                            className="chart-action-item__nav"
                            key={'listgroup' + parentIndex}
                            role="menu"
                        >
                            {dataList.Data.map((item, idx) => {

                                if (selectedReport === undefined && idx === 0) {

                                    setState({ selectedReport: item });
                                    item.Action && eval(item.Action);

                                }

                                return (
                                    <li
                                        className="chart-action-item__nav-item"
                                        key={idx}
                                        role="none"
                                    >
                                        <button
                                            onKeyDown={e => {

                                                if (e.key === 'Enter' || e.key === ' ') {

                                                    handleItemSelect(item);

                                                }

                                            }}
                                            aria-current={selectedReport === item ? 'true' : undefined}
                                            aria-label={item.Title}
                                            className={`chart-action-item__nav-btn ${selectedReport === item ? 'chart-action-item__nav-btn--active' : ''}`}
                                            onClick={() => handleItemSelect(item)}
                                            tabIndex={0}
                                            type="button"
                                        >
                                            <span className="chart-action-item__nav-icon">
                                                <i className={`fa-solid ${item.Share === '1' ? 'fa-check text-success' : 'fa-x text-danger'}`}></i>
                                            </span>
                                            <span className="chart-action-item__nav-label">
                                                {item.Title}
                                            </span>
                                        </button>
                                    </li>
                                );

                            })}
                        </ul>
                    ))
                )}
            </div>
        </div>
    );

    const renderSkeleton = () => (
        <div className="chart-action-item chart-action-item--skeleton">
            <div className="chart-action-item__title-skeleton" />
            {[1, 2, 3, 4, 5].map(item => (
                <div className="chart-action-item__skeleton-row" key={item}>
                    <span className="chart-action-item__skeleton-icon" />
                    <span className="chart-action-item__skeleton-text" />
                </div>
            ))}
        </div>
    );

    return (
        <>
            {currentDialog}
            <GMetrixDialog
                onCancel={() => {

                    setShowActionModal(false);
                    setState({ showModal: false });

                }}
                actionButtonDisabled={false}
                dialogLabel={formatMessage({ id: 'details.7799b923', defaultMessage: 'Details' })}
                show={showModal}
            >
                <iframe
                    className="chart-action-item__iframe"
                    src={embeddedURL}
                    title={formatMessage({ id: 'details.7799b923', defaultMessage: 'Details' })}
                />
            </GMetrixDialog>
            <Col
                className={`chart-action-item__col d-print-none ${isSubReport ? 'chart-action-item__col--sub-report' : ''}`}
                id={index.toString()}
                key={`${component.ComponentId}-${index}`}
                lg={isSubReport ? 12 : component.WidthLg}
                md={isSubReport ? 12 : component.WidthMd}
                sm={isSubReport ? 12 : component.WidthSm}
                xl={isSubReport ? 12 : component.WidthXl}
                xxl={component.WidthXl}
            >
                {!loading ? renderContent() : renderSkeleton()}
            </Col>
        </>
    );

};
